import { Injectable } from '@angular/core';
import posthog from 'posthog-js';

type DeviceType = 'mobile' | 'tablet' | 'desktop';

interface CommonEventProperties {
  device_type: DeviceType;
  viewport_width: number;
  viewport_height: number;
  language: string;
  timestamp: string;
}

interface SectionTimer {
  startTime: number;
  sectionName: string;
}

@Injectable({
  providedIn: 'root',
})
export class PosthogAnalyticsService {
  private sectionTimers: Map<string, SectionTimer> = new Map();

  /**
   * Detects the device type based on viewport width
   * @returns Device type classification
   */
  getDeviceType(): DeviceType {
    const width = window.innerWidth;

    if (width <= 768) {
      return 'mobile';
    } else if (width <= 1024) {
      return 'tablet';
    } else {
      return 'desktop';
    }
  }

  /**
   * Gets common properties that should be included with every event
   * @returns Object containing device info, viewport dimensions, language, and timestamp
   */
  getCommonProperties(): CommonEventProperties {
    return {
      device_type: this.getDeviceType(),
      viewport_width: window.innerWidth,
      viewport_height: window.innerHeight,
      language: navigator.language,
      timestamp: new Date().toISOString(),
    };
  }

  /**
   * Starts a timer for a specific section to track time spent
   * @param sectionName - Name of the section to track
   */
  startSectionTimer(sectionName: string): void {
    this.sectionTimers.set(sectionName, {
      startTime: Date.now(),
      sectionName,
    });
  }

  /**
   * Gets the time spent on a section in seconds
   * @param sectionName - Name of the section
   * @returns Time spent in seconds, or 0 if timer hasn't been started
   */
  getTimeOnSection(sectionName: string): number {
    const timer = this.sectionTimers.get(sectionName);

    if (!timer) {
      return 0;
    }

    const timeSpent = (Date.now() - timer.startTime) / 1000;
    return Math.round(timeSpent * 100) / 100; // Round to 2 decimal places
  }

  /**
   * Stops and clears the timer for a specific section
   * @param sectionName - Name of the section
   */
  stopSectionTimer(sectionName: string): void {
    this.sectionTimers.delete(sectionName);
  }

  /**
   * Clears all section timers
   */
  clearAllTimers(): void {
    this.sectionTimers.clear();
  }

  /**
   * Gets the current scroll position as a percentage
   * @returns Scroll position percentage (0-100)
   */
  getScrollPosition(): number {
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const scrollHeight =
      document.documentElement.scrollHeight - window.innerHeight;

    if (scrollHeight <= 0) {
      return 0;
    }

    return Math.round((scrollTop / scrollHeight) * 100);
  }

  /**
   * Tracks a page view with common properties
   * @param pageName - Name of the page
   * @param additionalProps - Additional properties to include
   */
  trackPageView(
    pageName: string,
    additionalProps: Record<string, any> = {},
  ): void {
    const properties = {
      ...this.getCommonProperties(),
      page: pageName,
      scroll_position: this.getScrollPosition(),
      ...additionalProps,
    };

    posthog.capture('$pageview', properties);
  }

  /**
   * Tracks a custom event with common properties automatically included
   * @param eventName - Name of the event
   * @param additionalProps - Additional properties specific to this event
   */
  captureWithContext(
    eventName: string,
    additionalProps: Record<string, any> = {},
  ): void {
    const properties = {
      ...this.getCommonProperties(),
      scroll_position: this.getScrollPosition(),
      ...additionalProps,
    };

    posthog.capture(eventName, properties);
  }

  /**
   * Tracks an event without adding common properties (for more control)
   * @param eventName - Name of the event
   * @param properties - Event properties
   */
  trackEvent(eventName: string, properties: Record<string, any> = {}): void {
    posthog.capture(eventName, properties);
  }
}
